import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { OverlayScrollbar } from "@/components/ui/overlay-scrollbar";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/contexts/AuthContext";
import {
  getNavForRole,
  homePathForRole,
  SECONDARY_ITEMS,
  type NavItem,
  type Role,
} from "./nav-config";
import { useTabNavigate } from "@/lib/tab-navigation";

interface SidebarProps {
  role: Role;
  /** Controlled collapse state (driven by Layout per breakpoint). */
  collapsed: boolean;
  onCollapsedChange?: (next: boolean) => void;
  /** Hide the toggle (e.g., on tablet where collapsed is forced) */
  showToggle?: boolean;
  /** Fired when a nav item is clicked — used by the mobile drawer to
   *  close itself after navigation. No-op on the desktop rail. */
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({
  role,
  collapsed,
  onCollapsedChange,
  showToggle = true,
  onNavigate,
  className,
}: SidebarProps) {
  const { canViewFeature } = usePermission();
  const { user } = useAuth();
  // Top-level nav uses Android bottom-nav semantics: tabs don't stack, home
  // stays at the base of the back stack. See @/lib/tab-navigation.
  const goTab = useTabNavigate(homePathForRole(role));
  const groups = React.useMemo(
    () =>
      getNavForRole(role, user?.rank)
        .map((g) => ({ ...g, items: g.items.filter((it) => !it.feature || canViewFeature(it.feature)) }))
        .filter((g) => g.items.length > 0),
    [role, canViewFeature, user?.rank],
  );
  const secondary = React.useMemo(() => SECONDARY_ITEMS(role), [role]);

  // Sidebar identity (logo, name, tagline) is app-branded — never replaced by
  // company branding. The company logo + name live on the top header instead
  // (see Header.tsx). The rail's *accent* colour does follow the company
  // theme: it reads `--sidebar-accent`, which BrandingContext derives from the
  // selected primary colour (see contexts/BrandingContext.tsx).
  const displayName = "iCheck";
  const tagline = "By Element Tree";
  const initials = "iC";

  return (
    <TooltipProvider delayDuration={120}>
      <aside
        data-collapsed={collapsed || undefined}
        className={cn(
          "h-full flex flex-col bg-sidebar-surface text-sidebar-foreground border-r border-sidebar-border/60",
          "transition-[width] duration-(--dur-base) ease-out",
          collapsed ? "w-15" : "w-70",
          className,
        )}
      >
        {/* Brand */}
        <div className={cn(
          "h-20 flex items-center border-b border-sidebar-border/60 shrink-0",
          collapsed ? "justify-center px-0" : "justify-between px-3",
        )}>
          {collapsed ? (
            <BrandBadge initials={initials} />
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <BrandBadge initials={initials} />
              <div className="flex flex-col min-w-0 leading-tight">
                <span className="text-[14px] font-semibold tracking-tight truncate text-white" title={displayName}>
                  {displayName}
                </span>
                <span className="text-[10px] text-sidebar-muted tracking-wide">
                  {tagline}
                </span>
              </div>
            </div>
          )}
          {showToggle && !collapsed ? (
            <button
              onClick={() => onCollapsedChange?.(true)}
              className="inline-flex size-7 items-center justify-center rounded-md text-sidebar-muted hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronsLeft className="size-4" />
            </button>
          ) : null}
        </div>
          
        {/* Nav — OverlayScrollbar draws a custom thumb that fades in on
            scroll/hover and fades out when idle (desktop only; touch keeps its
            native overlay bar). Thumb uses the light sidebar-foreground token
            so it reads on the dark rail. */}
        <OverlayScrollbar
          as="nav"
          data-tour="nav"
          className="flex-1"
          contentClassName="px-2 py-3 space-y-4"
          thumbClassName="bg-sidebar-muted/50 hover:bg-sidebar-muted/70"
        >
          {groups.map((group, gi) => (
            <div key={gi}>
              {group.label && !collapsed ? (
                <p className="px-2 mb-1 text-[10px] uppercase tracking-wider font-medium text-sidebar-muted/70">
                  {group.label}
                </p>
              ) : group.label && collapsed && gi > 0 ? (
                <div className="mx-2 my-2 border-t border-sidebar-border/60" aria-hidden="true" />
              ) : null}

              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavItemLink item={item} collapsed={collapsed} goTab={goTab} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </OverlayScrollbar>

        {/* Secondary */}
        <div className="px-2 py-3 border-t border-sidebar-border/60 space-y-0.5">
          {secondary.map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} goTab={goTab} onNavigate={onNavigate} />
          ))}
          {showToggle && collapsed ? (
            <button
              onClick={() => onCollapsedChange?.(false)}
              className="mt-1 w-full inline-flex items-center justify-center h-9 rounded-md text-sidebar-muted hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Expand sidebar"
            >
              <ChevronsRight className="size-4" />
            </button>
          ) : null}
        </div>
      </aside>
    </TooltipProvider>
  );
}

/** -------- Internal -------- */

function NavItemLink({ item, collapsed, goTab, onNavigate }: { item: NavItem; collapsed: boolean; goTab: (to: string) => void; onNavigate?: () => void }) {
  const location = useLocation();
  const isActive = item.matchPrefix
    ? location.pathname.startsWith(item.to)
    : location.pathname === item.to;
  const Icon = item.icon;

  // Intercept the push: preventDefault() stops React Router's default navigation
  // (Link skips it once the event is defaultPrevented), then goTab() runs the
  // non-stacking tab navigation. Keeping <NavLink to> preserves the href (for
  // right-click/open-in-new-tab and accessibility) and the active styling.
  const handleClick = (e: React.MouseEvent) => {
    // Let modified clicks (new tab/window) and non-primary buttons behave natively.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      onNavigate?.();
      return;
    }
    e.preventDefault();
    goTab(item.to);
    onNavigate?.();
  };

  const link = (
    <NavLink
      to={item.to}
      onClick={handleClick}
      className={cn(
        "group relative flex items-center gap-2.5 rounded-md px-2.5 h-9 text-[13px] font-medium",
        "transition-colors duration-(--dur-fast) ease-out",
        "outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        collapsed && "justify-center px-0",
        isActive
          ? "bg-sidebar-accent/15 text-white shadow-[inset_0_0_0_1px_hsl(var(--sidebar-accent)/0.25)]"
          : "text-sidebar-foreground/90 hover:text-white hover:bg-sidebar-accent/10",
      )}
      end={!item.matchPrefix}
    >
      <Icon
        className={cn(
          "size-4 shrink-0",
          isActive ? "text-sidebar-accent" : "text-sidebar-muted group-hover:text-white",
          item.ai && "text-ai-from",
        )}
        strokeWidth={isActive ? 2.2 : 1.8}
      />
      {!collapsed && (
        <>
          <span className="truncate">{item.label}</span>
          {item.ai ? (
            <Sparkles className="ml-auto size-3 text-ai-from opacity-70" aria-hidden="true" />
          ) : null}
        </>
      )}
      {/* Active accent rail (left edge) when expanded */}
      {!collapsed && isActive ? (
        <span
          className="absolute -left-2 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-sidebar-accent"
          aria-hidden="true"
        />
      ) : null}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

// BrandBadge — the 32x32 sidebar avatar. App-branded only //

function BrandBadge({ initials }: { initials: string }) {
  const [errored, setErrored] = React.useState(false);
  return (
    <div className="size-14 grid place-items-center shrink-0">
      {errored ? (
        <span className="rounded-md bg-primary text-primary-fg grid place-items-center font-bold text-sm">
          {initials}
        </span>
      ) : (
        <img
          src="/app-logo.png"
          alt="iCheck"
          className="size-8 rounded-md object-contain"
          onError={() => setErrored(true)}
        />
      )}
    </div>
  );
}
